import { get } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import { t } from 'ember-intl';
import { and, eq, not } from 'ember-truth-helpers';
import BvAvatar from 'volta/components/bv-avatar';
import BvInline from 'volta/components/bv-inline';
import BvSelect, { type BvSelectArgs } from 'volta/components/bv-select';
import BvSelectMulti from 'volta/components/bv-select-multi';
import BvText from 'volta/components/bv-text';
import UserListItem from 'volta/components/user/list-item';
import me from 'volta/helpers/me';
import orElse from 'volta/helpers/or-else';
import { sanitized } from 'volta/utils/text-utils';

import type User from 'volta/models/user';

type BvSelectProps = Pick<
  BvSelectArgs,
  | 'afterOptionsComponent'
  | 'disabled'
  | 'fill'
  | 'isLoading'
  | 'isRequired'
  | 'matchTriggerWidth'
  | 'onChange'
  | 'onOpen'
  | 'onOpen'
  | 'options'
  | 'placeholder'
  | 'size'
  | 'triggerClass'
  | 'selected'
  | 'searchField'
>;

interface UserSelectSignature {
  Args: BvSelectProps & {
    multiple: boolean;
  };
  Element: HTMLElement;
}

export default class UserSelectComponent extends Component<UserSelectSignature> {
  @action
  matchName(person: User, term: string) {
    return `${sanitized(person.firstName)} ${sanitized(person.middleName)} ${sanitized(
      person.lastName
    )} ${sanitized(person.login)} ${sanitized(person.email)}`.includes(sanitized(term))
      ? 1
      : -1;
  }

  <template>
    {{#let (get (me) 'id') as |myUserId|}}
      {{#if @multiple}}
        <BvSelectMulti
          @fill={{@fill}}
          @options={{@options}}
          @selected={{@selected}}
          @searchField={{orElse @searchField 'login'}}
          @placeholder={{@placeholder}}
          @matcher={{unless @searchField this.matchName}}
          @searchEnabled={{true}}
          @disabled={{@disabled}}
          @size={{@size}}
          @isLoading={{@isLoading}}
          @onChange={{optional @onChange}}
          @matchTriggerWidth={{orElse @matchTriggerWidth false}}
          @afterOptionsComponent={{@afterOptionsComponent}}
          @selectedItemComponent={{component UserListItem isSelected=true}}
          @isRequired={{@isRequired}}
          @onOpen={{@onOpen}}
          ...attributes
          as |user|
        >
          <UserListItem @option={{user}} />
        </BvSelectMulti>
      {{else}}
        <BvSelect
          @triggerClass={{@triggerClass}}
          @options={{@options}}
          @selected={{@selected}}
          @searchField={{orElse @searchField 'login'}}
          @matcher={{unless @searchField this.matchName}}
          @placeholder={{@placeholder}}
          @isLoading={{@isLoading}}
          @onChange={{optional @onChange}}
          @fill={{@fill}}
          @disabled={{@disabled}}
          @size={{@size}}
          @matchTriggerWidth={{orElse @matchTriggerWidth false}}
          @afterOptionsComponent={{@afterOptionsComponent}}
          @onOpen={{@onOpen}}
          @allowClear={{not @isRequired}}
          ...attributes
          as |user|
        >
          {{#let (eq myUserId user.userId) as |itsMe|}}
            <BvInline @blockAlign='center' @gap='2'>
              <BvAvatar
                @size='sm'
                @presence={{if user.connected 'online' 'offline'}}
                @firstName={{user.firstName}}
                @lastName={{user.lastName}}
                @login={{user.login}}
                @background={{if itsMe 'indigo'}}
              />
              <BvText class='u-ellipsis'>
                {{#if itsMe}}
                  {{t 'me'}}
                {{else if (and user.firstName user.lastName)}}
                  {{user.firstName}}{{user.lastName}}
                {{else}}
                  {{user.login}}
                {{/if}}
              </BvText>
            </BvInline>
          {{/let}}
        </BvSelect>
      {{/if}}
    {{/let}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    UserSelect: typeof UserSelectComponent;
    'user-select': typeof UserSelectComponent;
  }
}
